/**
 * 3D Designer — starting rooms.
 *
 * A `DesignerDoc` is everything the designer edits: the room's dimensions plus
 * its positioned sections. Presets build one from the same wizard-shaped config
 * the estimator uses (`createFullRoomWallConfigs` → `roomSurfacesFromWallConfigs`),
 * so a preset room is exactly what the wizard would have produced.
 *
 * Walls come out in the viewer's box order: [front, right, back, left].
 */

import { RoomDimensions, FullRoomWallConfig, SlatWallConfig } from '../../types';
import { CeilingProfile } from '../../calculations/ceiling/profile';
import { RoomSurfaces } from '../../types/sections';
import { createFullRoomWallConfigs } from '../../calculations/dimensions';
import { roomSurfacesFromWallConfigs, ceilingSurface } from '../../calculations/sections';

export interface DesignerDoc {
  dims: RoomDimensions;
  room: RoomSurfaces;
  /** The ceiling's shape (absent = flat). Applied by the host through `setCeilingProfile`. */
  ceilingProfile?: CeilingProfile;
}

export interface DesignerPreset {
  id: string;
  label: string;
  build: () => DesignerDoc;
}

const NO_SLATWALL: SlatWallConfig = { enabled: false, startHeight: 0, numPanelRows: 0, color: 'White', useCapTrim: false };
const NO_GARAGE: FullRoomWallConfig['garage'] = { enabled: false, width: 0, height: 0, numDoors: 0, onWallType: 'None' };

function buildRoom(
  dims: RoomDimensions,
  config: Pick<FullRoomWallConfig, 'wallColor' | 'wallOrientation'> & { slatWall?: SlatWallConfig },
  ceiling: 'White' | 'Gray' | null,
): DesignerDoc {
  const walls = createFullRoomWallConfigs(dims, {
    wallColor: config.wallColor,
    wallOrientation: config.wallOrientation,
    slatWall: config.slatWall ?? NO_SLATWALL,
    garage: NO_GARAGE,
  });
  const room = roomSurfacesFromWallConfigs(walls, dims.height);
  // No ceiling colour is a bare ceiling, as the planner leaves one: the surface
  // is there (to click and board, and for the render to close the room), empty.
  const top = ceilingSurface('ceiling', 'Ceiling', dims.length, dims.width, ceiling ?? 'White');
  room.ceiling = ceiling ? top : { ...top, sections: [] };
  return { dims, room };
}

export const DESIGNER_PRESETS: DesignerPreset[] = [
  {
    id: 'garage-slatwall',
    label: 'Garage 20 × 12 × 10 — SlatWall band',
    build: () => buildRoom(
      { length: 20, width: 12, height: 10 },
      {
        wallColor: 'White',
        wallOrientation: 'Horizontal',
        slatWall: { enabled: true, startHeight: 3, numPanelRows: 4, color: 'Gray', useCapTrim: false },
      },
      'White',
    ),
  },
  {
    id: 'plain',
    label: 'Plain 24 × 24 × 9 — boards only',
    build: () => buildRoom({ length: 24, width: 24, height: 9 }, { wallColor: 'White', wallOrientation: 'Horizontal' }, 'White'),
  },
  {
    id: 'vertical',
    label: 'Vertical boards 16 × 12 × 8 — bare ceiling',
    build: () => buildRoom({ length: 16, width: 12, height: 8 }, { wallColor: 'Gray', wallOrientation: 'Vertical' }, null),
  },
  {
    id: 'picture-frame',
    label: 'SlatWall panel inset 20 × 12 × 10',
    build: () => {
      const doc = buildRoom({ length: 20, width: 12, height: 10 }, { wallColor: 'White', wallOrientation: 'Horizontal' }, 'White');
      // A SlatWall rectangle floating inside the front wall's boards — the
      // resolver carves the boards around it.
      doc.room.walls[0].sections.push({
        id: `${doc.room.walls[0].id}-slatwall-inset`,
        productType: 'SlatWall',
        x: 6,
        y: 3,
        width: 8,
        height: 3,
        color: 'Gray',
      });
      return doc;
    },
  },
];

DESIGNER_PRESETS.push({
  id: 'garage-openings',
  label: 'Garage 24 × 20 × 10 — door, windows, garage door',
  build: () => {
    const doc = buildRoom(
      { length: 24, width: 20, height: 10 },
      {
        wallColor: 'White',
        wallOrientation: 'Horizontal',
        slatWall: { enabled: true, startHeight: 3, numPanelRows: 4, color: 'Gray', useCapTrim: false },
      },
      'White',
    );
    const [front, right, back] = doc.room.walls;
    // Back wall (the one facing you when the room opens): a door and two windows.
    back.openings = [
      { id: `${back.id}-door-1`, kind: 'door', x: 2, y: 0, width: 3, height: 80 / 12 },
      { id: `${back.id}-window-1`, kind: 'window', x: 9, y: 3.5, width: 4, height: 3.5 },
      { id: `${back.id}-window-2`, kind: 'window', x: 16, y: 3.5, width: 4, height: 3.5 },
    ];
    right.openings = [{ id: `${right.id}-garage-1`, kind: 'garageDoor', x: 2, y: 0, width: 16, height: 7 }];
    front.openings = [{ id: `${front.id}-window-1`, kind: 'window', x: 10, y: 3.5, width: 4, height: 3.5 }];
    return doc;
  },
});

DESIGNER_PRESETS.push({
  id: 'gable-garage',
  label: 'Gable garage 24 × 20 × 9 — 6/12 cathedral ceiling',
  build: () => {
    const doc = buildRoom(
      { length: 24, width: 20, height: 9 },
      {
        wallColor: 'White',
        wallOrientation: 'Horizontal',
        slatWall: { enabled: true, startHeight: 3, numPanelRows: 4, color: 'Gray', useCapTrim: false },
      },
      'White',
    );
    const [front] = doc.room.walls;
    front.openings = [{ id: `${front.id}-garage-1`, kind: 'garageDoor', x: 4, y: 0, width: 16, height: 7 }];
    // Ridge along the room's length, 6/12 across the 20' width: a 5' rise to a 14' peak.
    doc.ceilingProfile = { kind: 'gable', ridgeAxis: 'length', ridgeAt: 0.5, rise: 5 };
    return doc;
  },
});

export const DEFAULT_PRESET_ID = DESIGNER_PRESETS[0].id;

export function getPreset(id: string): DesignerPreset {
  return DESIGNER_PRESETS.find((p) => p.id === id) ?? DESIGNER_PRESETS[0];
}
